/**
 * Member In Department Service
 * Handles member-department view operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MemberInDepartmentService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MemberInDepartmentService.DEFAULT_SERVICE_PATH,
            basePath: MemberInDepartmentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL VIEWS ====================
    /**
     * Get all member-department views
     * @returns Array of member-department views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all member-department views (raw response)
     * @returns Raw response with array of member-department views
     */
    getAllViewsRaw() {
        const url = this.buildUrl('view');
        return this.http.get(url);
    }
    // ==================== GET VIEW BY COMPOSITE KEY ====================
    /**
     * Get member-department view by composite key
     * @param memberId - Member ID
     * @param departmentId - Department ID
     * @returns Member-department view details
     */
    async getView(memberId, departmentId) {
        const response = await this.getViewRaw(memberId, departmentId);
        if (!response.data) {
            throw new Error('Member-department assignment not found');
        }
        return response.data;
    }
    /**
     * Get member-department view by composite key (raw response)
     * @param memberId - Member ID
     * @param departmentId - Department ID
     * @returns Raw response with member-department view details
     */
    getViewRaw(memberId, departmentId) {
        const url = this.buildUrl(`view/${memberId}/${departmentId}`);
        return this.http.get(url);
    }
    // ==================== GET VIEWS BY MEMBER ====================
    /**
     * Get all department views for a member
     * @param memberId - Member ID
     * @returns Array of member-department views for the member
     */
    async getViewsByMember(memberId) {
        const response = await this.getViewsByMemberRaw(memberId);
        return response.data ?? [];
    }
    /**
     * Get all department views for a member (raw response)
     * @param memberId - Member ID
     * @returns Raw response with array of member-department views
     */
    getViewsByMemberRaw(memberId) {
        const url = this.buildUrl(`member/${memberId}/view`);
        return this.http.get(url);
    }
    // ==================== GET VIEWS BY DEPARTMENT ====================
    /**
     * Members of a department (active memberships only unless includeInactive). Sorted head first,
     * then by name. Admin, Member.Department or Department.
     * @param departmentId - Department ID
     * @param includeInactive - Also return removed (soft-deleted) memberships
     */
    async getViewsByDepartment(departmentId, includeInactive) {
        const response = await this.getViewsByDepartmentRaw(departmentId, includeInactive);
        return response.data ?? [];
    }
    /**
     * Get all member views for a department (raw response)
     * @param departmentId - Department ID
     * @returns Raw response with array of member-department views
     */
    getViewsByDepartmentRaw(departmentId, includeInactive) {
        const url = this.buildUrlWithParams(`department/${departmentId}/view`, includeInactive ? { includeInactive: true } : {});
        return this.http.get(url);
    }
    // ==================== LIST VIEW WITH PAGINATION ====================
    /**
     * Get filtered member-department views with pagination
     * @param filter - Filter criteria including pagination
     * @returns Paged result of member-department views
     */
    async getListView(filter) {
        const response = await this.getListViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve member-department list view');
        }
        return response.data;
    }
    /**
     * Get filtered member-department views with pagination (raw response)
     * @param filter - Filter criteria including pagination
     * @returns Raw response with paged result
     */
    getListViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter || {});
    }
}
MemberInDepartmentService.DEFAULT_SERVICE_PATH = 'departments';
MemberInDepartmentService.BASE_PATH = 'api/member-in-departments';
//# sourceMappingURL=MemberInDepartmentService.js.map